import { useEffect, useRef, useState } from "react";
import { Wrench, LogOut } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import { clearPrivateLocalState } from "@/utils/clearSession";
import { useMaintenanceStatus } from "../hooks/useMaintenanceStatus";
import { MaintenanceEndedModal } from "../components/MaintenanceEndedModal";

export function MaintenancePage() {
  const { data: status } = useMaintenanceStatus();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  const [showEndedModal, setShowEndedModal] = useState(false);

  // Track whether we ever saw active maintenance this session so we can
  // distinguish "maintenance just ended" from "navigated here directly"
  const seenActiveRef = useRef(false);

  useEffect(() => {
    if (!status) return;
    if (status.active) {
      seenActiveRef.current = true;
    } else if (seenActiveRef.current) {
      setShowEndedModal(true);
    } else {
      window.location.replace("/");
    }
  }, [status]);

  const window_ = status?.active;
  const endsAt = window_ ? new Date(window_.ends_at) : null;
  const endsLabel = endsAt
    ? endsAt.toLocaleString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const handleLogout = () => {
    logout({ deliberate: true });
    qc.clear();
    clearPrivateLocalState();
    window.location.replace("/auth/login");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--color-bg-base)] px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <Wrench size={32} className="text-red-600" />
          </div>
        </div>

        <div>
          <p className="text-xs font-mono text-[var(--color-text-muted)] uppercase tracking-widest mb-1">
            GK ELEKTIM · ELGW 5
          </p>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Przerwa techniczna
          </h1>
          {window_?.title && (
            <p className="mt-1 text-sm font-semibold text-[var(--color-text-secondary)]">
              {window_.title}
            </p>
          )}
        </div>

        {window_?.message && (
          <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
            {window_.message}
          </p>
        )}

        {endsLabel && (
          <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg px-4 py-3 text-sm">
            <span className="text-[var(--color-text-muted)]">
              Planowane zakończenie:{" "}
            </span>
            <span className="font-semibold text-[var(--color-text-primary)]">
              {endsLabel}
            </span>
          </div>
        )}

        <p className="text-xs text-[var(--color-text-muted)]">
          Strona odświeży się automatycznie po zakończeniu przerwy.
        </p>

        {user && (
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 mx-auto text-xs font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <LogOut size={13} />
            Wyloguj się
          </button>
        )}
      </div>
      <MaintenanceEndedModal open={showEndedModal} />
    </div>
  );
}
